import PropTypes from 'prop-types';
import { Link as RouterLink, useParams } from 'react-router-dom';
// @mui
import { Box, Tooltip, Link, ListItemText } from '@mui/material';
// locales
import { useLocales } from '../../../../locales';
// auth
import RoleBasedGuard from '../../../../auth/RoleBasedGuard';
//
import Iconify from '../../iconify';
//
import { StyledItem, StyledIcon, StyledDotIcon } from './styles';
import { useEventRelativeLink } from "../../../../hooks/useEventRelativeLink";
import { useAuthContext } from "../../../../context-state/AuthContext";
import { useAccountRelativeLink } from "../../../../hooks/useAccountRelativeLink";
import { useSettingsContext } from '../../../../components/minimal/settings';

// ----------------------------------------------------------------------

NavItem.propTypes = {
  open: PropTypes.bool,
  active: PropTypes.bool,
  item: PropTypes.object,
  depth: PropTypes.number,
  isExternalLink: PropTypes.bool,
};

const usePath = (path, isAccountRelative, isEventRelative) => {

  const eventRelativeLink = useEventRelativeLink()
  const accountRelativeLink = useAccountRelativeLink()

  if (isAccountRelative) {
    return `${accountRelativeLink}${path}`
  }

  if (isEventRelative) {
    return `${eventRelativeLink}${path}`
  }

  return path

}

export default function NavItem({ item, depth, open, active, isExternalLink, ...other }) {
  const { translate } = useLocales();
  const { account } = useAuthContext();
  const { themeDirection } = useSettingsContext();

  const { title, path, icon, info, children, disabled, caption, roles, isRelative, isAccountRelative, disableChildIndicator } = item;

  const fullPath = usePath(path, isAccountRelative, isRelative)


  const subItem = depth !== 1;
  const primaryTitle = (title === '__sub__' && account) ? account.name : `${translate(title)}`

  const renderContent = (
    <StyledItem depth={depth} active={active} disabled={disabled} caption={!!caption} {...other}>
      {icon && <StyledIcon>{icon}</StyledIcon>}

      {(subItem && !disableChildIndicator) && (
        <StyledIcon >
          <StyledDotIcon active={active && subItem} />
        </StyledIcon>
      )}

      <ListItemText
        primary={primaryTitle}
        secondary={
          caption && (
            <Tooltip title={`${translate(caption)}`} placement="top-start">
              <span>{`${translate(caption)}`}</span>
            </Tooltip>
          )
        }
        primaryTypographyProps={{
          noWrap: true,
          component: 'span',
          variant: active ? 'subtitle2' : 'body2',
          dir: themeDirection,
        }}
        secondaryTypographyProps={{
          noWrap: true,
          variant: 'caption',
          dir: themeDirection,
        }}
      />

      {info && (
        <Box component="span" sx={{ lineHeight: 0 }}>
          {info}
        </Box>
      )}

      {!!children && (
        <Iconify
          width={16}
          icon={open
            ? 'eva:arrow-ios-downward-fill'
            : themeDirection === 'ltr'
              ? 'eva:arrow-ios-forward-outline'
              : 'eva:arrow-ios-back-outline'
          }
          sx={{ ml: 1, flexShrink: 0 }}
        />
      )}
    </StyledItem>
  );

  const renderItem = () => {
    // ExternalLink
    if (isExternalLink)
      return (
        <Link href={fullPath} target="_blank" rel="noopener" underline="none">
          {renderContent}
        </Link>
      );

    // Has child
    if (children) {
      return renderContent;
    }

    // Default
    return (
      <Link component={RouterLink} to={fullPath} underline="none">
        {renderContent}
      </Link>
    );
  };

  return <RoleBasedGuard roles={roles}> {renderItem()} </RoleBasedGuard>;
}
